import type { PortfolioSnapshot } from '../lib/supabase'

interface Props {
  snapshot: PortfolioSnapshot | null
}

const SLEEVE_TARGETS = { etf: 40, stock: 30, ipo: 20, commod: 20, crypto: 10 }
const SLEEVE_COLORS = {
  etf:    { bar: '#7c4dff', label: '#a78bfa' },
  stock:  { bar: '#00bcd4', label: '#67e8f9' },
  ipo:    { bar: '#ff9100', label: '#fdba74' },
  commod: { bar: '#84cc16', label: '#bef264' },
  crypto: { bar: '#f43f5e', label: '#fb7185' },
}

const SLEEVES: Array<{ key: keyof typeof SLEEVE_TARGETS; label: string; field: keyof PortfolioSnapshot }> = [
  { key: 'etf',    label: 'ETF Sleeve',       field: 'etf_exposure_pct' },
  { key: 'stock',  label: 'Stock Sleeve',     field: 'stock_exposure_pct' },
  { key: 'ipo',    label: 'IPO Sleeve',       field: 'ipo_exposure_pct' },
  { key: 'commod', label: 'Commodity Sleeve', field: 'commod_exposure_pct' },
  { key: 'crypto', label: 'Crypto Sleeve',    field: 'crypto_exposure_pct' },
]

export function ExposureChart({ snapshot }: Props) {
  if (!snapshot) {
    return (
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Exposure Breakdown</h2>
        </div>
        <div className="empty-state">
          <div className="empty-state-text">No exposure data</div>
        </div>
      </div>
    )
  }

  const today = new Date().toLocaleDateString('en-CA')
  const isStale = snapshot.snapshot_date < today

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title">Exposure Breakdown</h2>
        <span className="card-badge">
          {snapshot.total_exposure_pct.toFixed(0)}% / 150% Target
        </span>
      </div>

      {isStale && (
        <div style={{
          background: 'var(--warning-glow, rgba(255, 145, 0, 0.1))',
          border: '1px solid var(--warning, #ff9100)',
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-sm) var(--space-md)',
          marginBottom: 'var(--space-md)',
          fontSize: '0.75rem',
          color: 'var(--warning, #ff9100)',
          fontFamily: 'var(--font-mono)',
        }}>
          ⚠ Stale — last snapshot {snapshot.snapshot_date}
        </div>
      )}

      <div className="exposure-bars">
        {SLEEVES.map(({ key, label, field }) => {
          const pct = Number(snapshot[field] ?? 0)
          const target = SLEEVE_TARGETS[key]
          const colors = SLEEVE_COLORS[key]
          const fillPct = Math.min((pct / target) * 100, 100)
          return (
            <div key={key} className="exposure-bar">
              <div className="exposure-bar-header">
                <span className="exposure-bar-label" style={{ color: colors.label }}>{label}</span>
                <span className="exposure-bar-value">{pct.toFixed(1)}% / {target}%T</span>
              </div>
              <div className="exposure-bar-track">
                <div
                  className={`exposure-bar-fill ${key}`}
                  style={{ width: `${fillPct}%`, background: colors.bar }}
                />
                <div className="exposure-bar-limit" style={{ left: '100%' }} title={`Target: ${target}%`} />
              </div>
            </div>
          )
        })}

        <div className="exposure-bar" style={{ marginTop: '0.5rem' }}>
          <div className="exposure-bar-header">
            <span className="exposure-bar-label">Total Exposure</span>
            <span className="exposure-bar-value" style={{
              color: snapshot.total_exposure_pct > 150 ? 'var(--negative)' :
                     snapshot.total_exposure_pct > 125 ? 'var(--warning)' : 'var(--text-primary)',
            }}>
              {snapshot.total_exposure_pct.toFixed(1)}%
            </span>
          </div>
          <div className="exposure-bar-track">
            <div
              className="exposure-bar-fill total"
              style={{ width: `${Math.min((snapshot.total_exposure_pct / 150) * 100, 100)}%` }}
            />
            <div className="exposure-bar-limit" style={{ left: `${(100 / 150) * 100}%` }} title="100% gross" />
            <div className="exposure-bar-limit" style={{ left: '100%', background: 'var(--negative)' }} title="Max: 150%" />
          </div>
        </div>
      </div>
    </div>
  )
}